<script lang="ts" setup>
import type { VNode } from 'vue';

const isDesktop = useIsDesktop();

const emit = defineEmits<{
  (event: 'mouseenter'): void;
  (event: 'mouseleave'): void;
  (event: 'update:index', index: number): void;
  (event: 'previous', index: number): void;
  (event: 'next', index: number): void;
  (event: 'start', index: number): void;
  (event: 'end', index: number): void;
}>();
const props = withDefaults(
  defineProps<{
    disabled?: boolean;
    index?: number | null;
    id?: string | null;
    interval?: number | false;
    loop?: boolean;
    showCounter?: boolean;
    showStart?: boolean;
    showEnd?: boolean;
    showPrevious?: boolean;
    showNext?: boolean;
    showNavigation?: boolean;
    showConversionOverlay?: boolean;
    dataTrackingId?: string | string[];
    dataPage?: string;
  }>(),
  {
    disabled: false,
    index: null,
    id: '',
    interval: false,
    loop: true,
    showCounter: false,
    showStart: false,
    showEnd: false,
    showPrevious: true,
    showNext: true,
    showNavigation: false,
    showConversionOverlay: false,
    dataTrackingId: '',
    dataPage: '',
  },
);

const slots = useSlots();
const defaultSlots = computed(() =>
  slots.default
    ? slots
        .default()
        .map((o: VNode) => o.children)
        .filter((o) => !!o)
        .flat()
    : [],
);
const slideListItems = ref<HTMLLIElement[]>([]);
const id = computed(() => `TwCarousel_${props.id}`);
const slideCount = computed(() => defaultSlots.value.length || 1);
const elmScrollContainer = ref<HTMLElement | null>(null);

const internalIndex = ref(props.index || 0);
const index = computed({
  get() {
    return props.index !== null ? props.index : internalIndex.value;
  },
  set(v: number) {
    if (v < 0 || v >= defaultSlots.value.length) return;
    const ov = internalIndex.value;
    if (slideListItems.value[v]) slideListItems.value[v].scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'nearest' });
    internalIndex.value = v;
    emit('update:index', v);

    if (v === 0) emit('start', v);
    else if (v === ov + 1) emit('next', v);
    if (slots.default && v === slots.default.length) emit('end', v);
    else if (v === ov - 1) emit('previous', v);
  },
});

const previous = () => (index.value = props.loop && index.value === 0 ? defaultSlots.value.length - 1 : index.value !== 0 ? index.value - 1 : index.value);
const next = () =>
  (index.value =
    props.loop && index.value === defaultSlots.value.length - 1
      ? (index.value = 0)
      : index.value !== defaultSlots.value.length - 1
        ? index.value + 1
        : index.value);
const start = () => (index.value = 0);
const end = () => (index.value = defaultSlots.value.length - 1);

const onScroll = () => {
  if (elmScrollContainer.value) {
    const rect = elmScrollContainer.value.getBoundingClientRect();
    const currentSlide = Math.round(elmScrollContainer.value.scrollLeft / rect.width);
    if (index.value !== currentSlide) internalIndex.value = currentSlide;
  }
};

onMounted(() => {
  if (elmScrollContainer.value) {
    elmScrollContainer.value.scrollTo(0, 0);
    elmScrollContainer.value.addEventListener('scroll', onScroll);
  }
});
onUnmounted(() => elmScrollContainer.value && elmScrollContainer.value.removeEventListener('scroll', onScroll));

const { class: hoverClass, events: hoverEvents } = useHover(emit, props);
</script>

<template>
  <section :id="id" :class="['tw-carousel', hoverClass, !isDesktop ? 'is--mobile' : false]" v-on="{ ...hoverEvents }">
    <div ref="elmScrollContainer" class="tw-carousel--content" role="region">
      <ul class="tw-carousel--slide-list">
        <li
          v-for="(slot, idx) in defaultSlots"
          :id="`${id}_Slide_${idx + 1}`"
          style="position: relative"
          :key="`${id}_Slide_${idx + 1}`"
          ref="slideListItems"
          :class="['tw-carousel--slide', index === idx && 'is--active']"
          :data-slide="idx + 1"
        >
          <component :is="slot" :style="{ filter: idx > 2 && showConversionOverlay ? 'blur(14px)' : 'none' }" />
          <tw-ui-carousel-conversion-overlay
            v-if="showConversionOverlay && idx > 2"
            :data-tracking-id="dataTrackingId"
            :data-page="dataPage"
          />
        </li>
      </ul>
    </div>

    <div v-if="defaultSlots.length > 1" class="tw-carousel--controls-left">
      <tw-ui-button
        v-if="props.showStart"
        :aria-label="$t('carousel.go_to.start')"
        :disabled="props.disabled"
        class="action--start tw-box-variant--box"
        @click="start"
      >
        <Icon name="bi:chevron-double-left" class="chevron" />
      </tw-ui-button>
      <tw-ui-button
        v-if="props.showPrevious"
        :aria-label="$t('carousel.go_to.previous')"
        :disabled="props.disabled"
        class="action--previous tw-box-variant--box"
        @click="previous"
      >
        <Icon name="bi:chevron-left" class="chevron" />
      </tw-ui-button>
    </div>

    <div v-if="defaultSlots.length > 1" class="tw-carousel--controls-right">
      <tw-ui-button
        v-if="props.showNext"
        :aria-label="$t('carousel.go_to.next')"
        :disabled="props.disabled"
        class="action--next tw-box-variant--box"
        @click="next"
      >
        <Icon name="bi:chevron-right" class="chevron" />
      </tw-ui-button>
      <tw-ui-button v-if="props.showEnd" :aria-label="$t('carousel.go_to.end')" :disabled="props.disabled" class="action--end tw-box-variant--box" @click="end">
        <Icon name="bi:chevron-double-right" class="chevron" />
      </tw-ui-button>
    </div>

    <nav v-if="defaultSlots.length > 1 && props.showNavigation" class="tw-carousel--navigation">
      <ul class="tw-carousel--navigation-list">
        <template v-for="(_, idx) in defaultSlots" :key="`${id}_PaginationLink_${idx + 1}`">
          <li v-if="idx >= index - 2 && idx <= index + 2" :class="{ 'is--active': index === idx }" :slide="idx + 1" class="tw-carousel--navigation-list-item">
            <tw-ui-button :aria-label="$t('carousel.go_to.x', [idx + 1])" class="tw-box-variant--circle" @click="index = +idx" />
          </li>
        </template>
      </ul>
    </nav>

    <div v-if="defaultSlots.length > 1" class="tw-carousel--controls-counter">
      <tw-ui-button v-if="props.showCounter" class="action--counter tw-box-variant--badge" @click="next">
        {{ $t('common.phrases.x_of_y_short', [index + 1, defaultSlots.length]) }}
      </tw-ui-button>
    </div>
  </section>
</template>

<style lang="scss" scoped>
@import '@/assets/styles/tw-variables.scss';
@import '@/assets/styles/tw-mixins.scss';

.tw-carousel {
  position: relative;
  width: 100%;
  overflow: hidden;

  .tw-carousel--content,
  .tw-carousel--slide-list,
  .tw-carousel--slide,
  .tw-carousel--slide :slotted(*) {
    height: 100%;
  }

  .tw-carousel--content,
  .tw-carousel--slide-list,
  .tw-carousel--slide,
  .tw-carousel--navigation-list {
    margin: 0;
    padding: 0;
    list-style-type: none;
  }

  .tw-carousel--content {
    scroll-snap-type: x mandatory;
    scroll-snap-stop: always;
    scroll-behavior: auto;
    overflow: scroll;
    overflow-y: hidden;
    /* hide scrollbar but allow scrolling */
    //-ms-overflow-style: none; /* for Internet Explorer, Edge */
    //scrollbar-width: none; /* for Firefox */

    &::-webkit-scrollbar {
      display: none;
      /* for Chrome, Safari, and Opera */
    }
  }

  .tw-carousel--slide-list {
    display: flex;
    flex-flow: row nowrap;
    width: calc(100% * v-bind(slideCount));
  }

  .tw-carousel--slide {
    width: calc(100% / v-bind(slideCount));
    scroll-snap-align: start;

    &.is--active {
      opacity: 1;
    }

    > :deep(*) {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .tw-carousel--controls-left,
  .tw-carousel--controls-right,
  .tw-carousel--navigation,
  .tw-carousel--controls-counter {
    position: absolute;
  }

  .tw-carousel--controls-left,
  .tw-carousel--controls-right {
    top: 0;
    bottom: 0;
    display: flex;
    flex-flow: row nowrap;
    align-items: stretch;
    gap: $tw-component--gap;
    z-index: 1;

    .tw-button {
      padding: calc(#{$tw-component--gap} * 2);
      font-size: 1.5em;
      align-items: center;
      background-color: transparent;
      border-color: transparent;
      color: $lightGray;

      &.is--hovered {
        color: $primary;
      }
    }
  }

  .tw-carousel--controls-left {
    left: 0;
  }

  .tw-carousel--controls-right {
    right: 0;
  }

  .tw-carousel--navigation {
    bottom: calc(#{$tw-component--gap} * 2);
    left: calc(#{$tw-component--gap} * 2);
    right: calc(#{$tw-component--gap} * 2);
    z-index: 3;

    .tw-carousel--navigation-list {
      display: flex;
      flex-flow: row nowrap;
      align-items: center;
      justify-content: center;
      gap: $tw-component--gap;

      .tw-button {
        padding: calc(#{$tw-component--gap} * 1.5);
        background-color: $lightGray;
        transition:
          padding 0.15s linear,
          background-color 0.15s linear;
      }

      .is--active {
        .tw-button {
          padding: calc(#{$tw-component--gap} * 2);
          background-color: $primary;
        }
      }
    }
  }

  .tw-carousel--controls-counter {
    bottom: calc(#{$tw-component--gap} * 2);
    left: calc(#{$tw-component--gap} * 2);
    z-index: 5;

    .tw-button {
      font-size: 0.85em;
      padding: calc(#{$tw-component--gap} / 1.5) calc(#{$tw-component--gap} * 1.5);
      background-color: $lightGray;
      border-color: $lightGray;
      color: $black;
    }
  }
}

.action--start,
.action--previous,
.action--next,
.action--end {
  &:focus,
  &:hover,
  &:active {
    border: none;
    outline: none;
  }
}

.chevron {
  width: 2rem;
  height: 2rem;
}

.blurred {
  filter: blur(5px);
}
</style>
